import React, { useCallback, useEffect, useRef } from "react";
import "./MultiRangeSlider.css";
import { FILTER_MAX_PRICE, FILTER_MIN_PRICE } from "@/lib/constants/search";

export type OnChangeMultiRangeSlider = {
    min: number;
    max: number;
};

export type MultiRangeSliderOnChangeEvent = ({ min, max }: OnChangeMultiRangeSlider) => void;

type Props = {
    min: number;
    max: number;
    onChange: MultiRangeSliderOnChangeEvent;
    minValue?: number; // New prop for dynamic min value
    maxValue?: number; // New prop for dynamic maximum value
};

const MultiRangeSlider = ({ min, max, onChange, minValue, maxValue }: Props) => {
    const minimum = minValue??  FILTER_MIN_PRICE;
    const maximum = maxValue ?? FILTER_MAX_PRICE; // Use maxValue if provided, otherwise default to FILTER_MAX_PRICE
    const range = useRef<HTMLDivElement | null>(null);

    const getPercent = useCallback(
        (value: number) => Math.round(((value - minimum) / (maximum - minimum)) * 100),
        [minimum, maximum],
    );

    // Set width of the range based on the min and max values
    useEffect(() => {
        const minPercent = getPercent(min);
        const maxPercent = getPercent(max);
        if (range.current) {
            range.current.style.left = `${minPercent}%`;
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [min, max, getPercent]);

    return (
        <div className="grow relative">
            <input
                type="range"
                min={minimum}
                max={maximum}
                value={min}
                onChange={(event) => {
                    const value = Math.min(Number(event.target.value), max - 1);
                    onChange({ min: value, max });
                }}
                className="thumb thumb--left absolute w-full"
                style={{ zIndex: min > maximum - 100 ? "20" : "" }}
            />
            <input
                type="range"
                min={minimum}
                max={maximum}
                value={max}
                onChange={(event) => {
                    const value = Math.max(Number(event.target.value), min + 1);
                    onChange({ min, max: value });
                }}
                className="thumb thumb--right absolute w-full bg-cyan-500"
            />

            <div className="slider w-full relative">
                <div className="slider__track" />
                <div ref={range} className="slider__range bg-cyan-500" />
            </div>
        </div>
    );
};

export default MultiRangeSlider;
